import {
  ACP_AGENT_CATALOG,
  acpAgentCatalogEntryForAlias,
  acpAgentCatalogEntryForId,
} from '@crowdex/agent-contracts';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { ConnectionSummary } from '@crowdex/plugin-contracts';
import { useEffect, useState } from 'react';
import { useCrowlingoAccount } from '@/components/sidebar/crowlingo-account';
import { useDesktopUpdateStatus } from '@/hooks/use-desktop-update-status';
import { electronPlatform, isElectronDesktop } from '@/lib/electron-bridge';
import { CONNECTIONS_CHANGED_EVENT } from '@/lib/plugins/connection-events';

/** The default agent as the user reads it (`claude` → Claude Code); `auto` picks Claude Code. */
export function defaultAgentLabel(defaultAgent: string): string {
  // Automatic starts with the catalog's first default agent (Claude Code) when it is there.
  const entry =
    defaultAgent === 'auto'
      ? (ACP_AGENT_CATALOG.find((candidate) => candidate.default) ?? null)
      : (acpAgentCatalogEntryForAlias(defaultAgent) ?? acpAgentCatalogEntryForId(defaultAgent));
  if (entry !== null) return entry.name;
  // An agent the catalog does not know: its id, as words (`mistral-vibe` → Mistral vibe).
  const words = defaultAgent.replace(/^registry:/, '').replace(/[-_]+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Whether an account must be connected again before what uses it works. */
function needsReconnect(connection: ConnectionSummary): boolean {
  return (
    connection.status === 'needs_reauthorization' ||
    connection.status === 'revoked' ||
    connection.declaration === 'changed'
  );
}

/** The counts the column shows: accounts to reconnect. */
export function useSettingsCounts() {
  const [reconnect, setReconnect] = useState(0);
  useEffect(() => {
    let live = true;
    const loadConnections = () =>
      apiFetch<{ connections: ConnectionSummary[] }>('/api/connections').then(
        (listing) => live && setReconnect(listing.connections.filter(needsReconnect).length),
        () => live && setReconnect(0),
      );
    void loadConnections();
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, loadConnections);
    return () => {
      live = false;
      window.removeEventListener(CONNECTIONS_CHANGED_EVENT, loadConnections);
    };
  }, []);
  return { reconnect };
}

export type OrganizationSummary = { name: string; role: 'admin' | 'member' } | null;

/**
 * The user's organization: its name, and whether they are its admin (who may change its plugin
 * policy); `null` outside an organization.
 */
export function useOrganization(): OrganizationSummary {
  const [role, setRole] = useState<'admin' | 'member' | null>(null);
  const name = useCrowlingoAccount()?.organization?.name ?? null;
  useEffect(() => {
    let live = true;
    apiFetch<{ canEdit: boolean }>('/api/organizations/plugin-policy', {
      credentials: 'include',
    }).then(
      (policy) => live && setRole(policy.canEdit ? 'admin' : 'member'),
      () => live && setRole(null),
    );
    return () => {
      live = false;
    };
  }, []);
  return role === null ? null : { name: name ?? 'Your organization', role };
}

const PLATFORM_NAMES = { macos: 'macOS', windows: 'Windows', linux: 'Linux' } as const;

/** The column's last line: which Crowdex this is, and whether it is up to date. */
export function useVersionLine(): string {
  const update = useDesktopUpdateStatus();
  if (isElectronDesktop()) {
    const name = `Crowdex for ${PLATFORM_NAMES[electronPlatform()] ?? 'desktop'}`;
    if (update === null) return name;
    if (update.state === 'up-to-date' || update.state === 'idle') return `${name} · up to date`;
    if (update.state === 'available' || update.state === 'ready')
      return `${name} · update available`;
    if (update.state === 'downloading') return `${name} · updating`;
    return `${name} · ${update.currentVersion}`;
  }
  const installed =
    typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches;
  return installed ? 'Crowdex · installed app · up to date' : 'Crowdex on the web · up to date';
}
